import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Building2, 
  Search, 
  Plus, 
  Filter,
  Download,
  MoreVertical,
  TrendingUp,
  DollarSign,
  Calendar,
  Clock,
  Target,
  CheckCircle,
  AlertCircle,
  XCircle,
  Eye,
  Edit,
  Trash2,
  FileText,
  Users,
  BarChart3,
  Award,
  Sparkles,
  Activity,
  Zap,
  ExternalLink,
  Star,
  Archive
} from 'lucide-react';
import { Button } from '../../components/ui/Button/Button';
import { cn } from '../../utils/cn';

// Contract interface
interface Contract {
  id: string;
  title: string;
  client: {
    name: string;
    company: string;
    email: string;
  };
  value: number;
  status: 'negotiating' | 'signed' | 'under-review' | 'expired' | 'cancelled';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  stage: 'proposal' | 'negotiation' | 'review' | 'signed' | 'delivered';
  startDate: string;
  endDate: string;
  probability: number;
  description: string;
  tags: string[];
  createdAt: string;
  updatedAt: string;
}

interface ContractStats {
  totalContracts: number;
  totalPipelineValue: number;
  signedValue: number;
  winRate: number;
  avgDealSize: number;
  activeContracts: number;
}

// Animated counter component
const AnimatedCounter: React.FC<{ end: number; duration?: number; prefix?: string; suffix?: string }> = ({ 
  end, 
  duration = 2000, 
  prefix = "", 
  suffix = ""
}) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    let startTime: number;
    let animationFrame: number;

    const animate = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      
      setCount(Math.floor(progress * end));
      
      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      }
    };

    animationFrame = requestAnimationFrame(animate);
    
    return () => cancelAnimationFrame(animationFrame);
  }, [end, duration]);

  return (
    <span className="font-bold">
      {prefix}{count.toLocaleString()}{suffix}
    </span>
  );
};

// Metric Card Component
const MetricCard: React.FC<{
  title: string;
  value: string | number;
  change: string;
  icon: React.ElementType;
  gradient: string;
  description?: string;
  percentage?: string;
}> = ({ title, value, change, icon: Icon, gradient, description, percentage }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -4, scale: 1.02 }}
      className="group relative p-6 bg-gradient-to-br from-neutral-900/80 to-neutral-800/50 backdrop-blur-xl border border-neutral-700/50 rounded-2xl hover:border-neutral-600/50 transition-all duration-300 shadow-xl hover:shadow-2xl"
    >
      <div className="flex items-start justify-between">
        <div>
          <p className="text-neutral-400 text-sm font-medium mb-2">{title}</p>
          <p className="text-3xl font-bold text-white mb-1">
            {typeof value === 'number' ? (
              <AnimatedCounter end={value} />
            ) : (
              value
            )}
          </p>
          <div className="flex items-center space-x-2">
            <span className="text-green-400 text-sm font-medium">{change}</span>
            <TrendingUp className="w-4 h-4 text-green-400" />
          </div>
          {description && (
            <p className="text-neutral-500 text-xs mt-2">{description}</p>
          )}
        </div>
        <div className="flex flex-col items-end">
          <div className={cn("w-12 h-12 rounded-xl flex items-center justify-center mb-2", gradient)}>
            <Icon className="w-6 h-6 text-white" />
            </div>
          {percentage && (
            <span className="text-2xl font-bold text-white">{percentage}</span>
          )}
        </div>
      </div>
      
      {/* Hover effect overlay */}
      <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 to-purple-500/5 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
    </motion.div>
  );
};

// Contract Card Component
const ContractCard: React.FC<{
  contract: Contract;
  onView: (contract: Contract) => void;
  onEdit: (contract: Contract) => void;
  onDelete: (contract: Contract) => void;
}> = ({ contract, onView, onEdit, onDelete }) => {
  const [showActions, setShowActions] = useState(false);
  
  const getStatusConfig = (status: string) => {
    switch (status) {
      case 'signed':
        return {
          color: 'text-green-400 bg-green-500/20 border-green-500/30',
          icon: CheckCircle,
          label: 'Signed'
        };
      case 'negotiating':
        return {
          color: 'text-purple-400 bg-purple-500/20 border-purple-500/30',
          icon: Target,
          label: 'Negotiating'
        };
      case 'under-review':
        return {
          color: 'text-yellow-400 bg-yellow-500/20 border-yellow-500/30',
          icon: Clock,
          label: 'Under Review'
        };
      case 'expired':
        return {
          color: 'text-red-400 bg-red-500/20 border-red-500/30',
          icon: XCircle,
          label: 'Expired'
        };
      default:
        return {
          color: 'text-neutral-400 bg-neutral-500/20 border-neutral-500/30',
          icon: XCircle,
          label: 'Cancelled'
        };
    }
  };

  const getPriorityConfig = (priority: string) => {
    switch (priority) {
      case 'urgent':
        return 'text-red-400 bg-red-500/20';
      case 'high':
        return 'text-orange-400 bg-orange-500/20';
      case 'medium':
        return 'text-blue-400 bg-blue-500/20';
      default:
        return 'text-neutral-400 bg-neutral-500/20';
    }
  };

  const statusConfig = getStatusConfig(contract.status);
  const StatusIcon = statusConfig.icon;
  const priorityConfig = getPriorityConfig(contract.priority);

  const getCompanyInitials = (company: string) => {
    return company
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .substring(0, 2)
      .toUpperCase();
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -2, scale: 1.01 }}
      className="group relative p-6 bg-gradient-to-br from-neutral-900/60 to-neutral-800/40 backdrop-blur-xl border border-neutral-700/40 rounded-2xl hover:border-neutral-600/50 transition-all duration-300 shadow-lg hover:shadow-xl"
    >
      {/* Contract Header */}
        <div className="flex items-start justify-between mb-4">
        <div className="flex items-start space-x-4 flex-1 min-w-0">
          <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center flex-shrink-0">
            <span className="text-white text-sm font-semibold">
              {getCompanyInitials(contract.client.company)}
            </span>
              </div>
          <div className="min-w-0 flex-1">
            <h3 className="text-lg font-semibold text-white mb-1 truncate">
                  {contract.title}
                </h3>
            <p className="text-neutral-400 text-sm mb-2">
              {contract.client.company} • {contract.client.name}
                </p>
            <div className="flex items-center space-x-2 mb-2">
              <span className={cn(
                "inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border backdrop-blur-xl",
                statusConfig.color
              )}>
                <StatusIcon className="w-3 h-3 mr-1" />
                {statusConfig.label}
              </span>
              <span className={cn(
                "px-2 py-1 rounded-full text-xs font-medium",
                priorityConfig
              )}>
                {contract.priority.charAt(0).toUpperCase() + contract.priority.slice(1)} Priority
              </span>
            </div>
            </div>
          </div>
          
          <div className="relative">
          <button
              onClick={() => setShowActions(!showActions)}
            className="p-2 text-neutral-400 hover:text-white hover:bg-neutral-800/50 rounded-lg transition-colors"
          >
            <MoreVertical className="w-4 h-4" />
          </button>
            
            <AnimatePresence>
              {showActions && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95, y: -10 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95, y: -10 }}
                className="absolute right-0 top-full mt-2 w-48 bg-neutral-900/95 backdrop-blur-xl border border-neutral-700 rounded-xl shadow-2xl z-10"
                >
                <div className="p-2">
                    <button
                    onClick={() => onView(contract)}
                    className="w-full flex items-center space-x-3 px-3 py-2 text-neutral-300 hover:text-white hover:bg-neutral-800/50 rounded-lg transition-colors text-left"
                    >
                    <Eye className="w-4 h-4" />
                    <span className="text-sm">View</span>
                    </button>
                    <button
                    onClick={() => onEdit(contract)}
                    className="w-full flex items-center space-x-3 px-3 py-2 text-neutral-300 hover:text-white hover:bg-neutral-800/50 rounded-lg transition-colors text-left"
                    >
                    <Edit className="w-4 h-4" />
                    <span className="text-sm">Edit</span>
                    </button>
                  <div className="border-t border-neutral-700 my-1" />
                    <button
                    onClick={() => onDelete(contract)}
                    className="w-full flex items-center space-x-3 px-3 py-2 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg transition-colors text-left"
                  >
                    <Trash2 className="w-4 h-4" />
                    <span className="text-sm">Delete</span>
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

      {/* Contract Details */}
      <div className="mb-4">
        <p className="text-sm text-neutral-400 line-clamp-2 mb-3">
          {contract.description}
        </p>

        {/* Progress Bar */}
        <div className="mb-3">
          <div className="flex justify-between text-xs text-neutral-400 mb-1">
            <span>Deal Stage: {contract.stage.charAt(0).toUpperCase() + contract.stage.slice(1)}</span>
            <span>{contract.probability}% probability</span>
          </div>
          <div className="w-full bg-neutral-700 rounded-full h-2">
            <div 
              className="bg-gradient-to-r from-blue-500 to-purple-600 h-2 rounded-full transition-all duration-500"
              style={{ width: `${contract.probability}%` }}
            />
          </div>
        </div>
        </div>

      {/* Contract Value & Timeline */}
        <div className="grid grid-cols-2 gap-4 mb-4">
        <div>
          <p className="text-sm text-neutral-400 mb-1">Contract Value</p>
          <p className="text-xl font-bold text-green-400">
              ${contract.value.toLocaleString()}
          </p>
          <p className="text-xs text-neutral-500">Net 30</p>
            </div>
        <div>
          <p className="text-sm text-neutral-400 mb-1">Timeline</p>
          <p className="text-sm text-white">
            {new Date(contract.startDate).toLocaleDateString()} - {new Date(contract.endDate).toLocaleDateString()}
          </p>
          <p className="text-xs text-red-400">
            {new Date(contract.endDate) < new Date() ? 'Expired' : 'Active'}
          </p>
            </div>
          </div>
          
      {/* Tags */}
      {contract.tags.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-4">
          {contract.tags.slice(0, 3).map((tag, index) => (
            <span
              key={index}
              className="px-2 py-1 bg-neutral-800/50 text-neutral-300 text-xs rounded-full"
            >
              {tag}
            </span>
          ))}
          {contract.tags.length > 3 && (
            <span className="px-2 py-1 bg-neutral-800/50 text-neutral-400 text-xs rounded-full">
              +{contract.tags.length - 3} more
            </span>
          )}
        </div>
      )}

      {/* Footer */}
      <div className="flex items-center justify-between pt-4 border-t border-neutral-700/50">
        <span className="text-xs text-neutral-500">
          Updated {new Date(contract.updatedAt).toLocaleDateString()}
        </span>
          <div className="flex items-center space-x-2">
          <Button variant="outline" size="sm" className="border-neutral-600 text-neutral-300">
            <ExternalLink className="w-3 h-3 mr-1" />
              View
            </Button>
          <Button variant="outline" size="sm" className="border-neutral-600 text-neutral-300">
            <Edit className="w-3 h-3 mr-1" />
              Edit
            </Button>
        </div>
          </div>
          
      {/* Hover effect overlay */}
      <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 to-purple-500/5 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
    </motion.div>
  );
};

export const ContractsPage: React.FC = () => {
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('All Status');
  const [stageFilter, setStageFilter] = useState<string>('All Stages');
  const [stats, setStats] = useState<ContractStats>({
    totalContracts: 3,
    totalPipelineValue: 220000,
    signedValue: 25000,
    winRate: 100,
    avgDealSize: 73333,
    activeContracts: 2
  });

  const loadContracts = async () => {
    setLoading(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const mockContracts: Contract[] = [
        {
          id: '1',
          title: 'Enterprise Marketing Automation Package',
          client: {
            name: 'John Smith',
            company: 'TechCorp Solutions',
            email: 'john@techcorp.com'
          },
          value: 150000,
          status: 'negotiating',
          priority: 'high',
          stage: 'negotiation',
          startDate: '2024-01-31',
          endDate: '2025-01-30',
          probability: 75,
          description: 'Comprehensive marketing automation solution for enterprise client',
          tags: ['enterprise', 'marketing', 'automation'],
          createdAt: '2024-01-01T00:00:00Z',
          updatedAt: '2024-01-15T00:00:00Z'
        },
        {
          id: '2',
          title: 'SMB Lead Generation Package',
          client: {
            name: 'Sarah Johnson',
            company: 'Growth Dynamics',
            email: 'sarah@growthdynamics.com'
          },
          value: 25000,
          status: 'signed',
          priority: 'medium',
          stage: 'signed',
          startDate: '2024-01-14',
          endDate: '2024-06-14',
          probability: 100,
          description: 'Lead generation and outreach services for small business',
          tags: ['smb', 'lead-generation'],
          createdAt: '2024-01-10T00:00:00Z',
          updatedAt: '2024-01-20T00:00:00Z'
        },
        {
          id: '3',
          title: 'SaaS Startup Outreach Campaign',
          client: {
            name: 'Mike Chen',
            company: 'InnovaTech',
            email: 'mike@innovatech.io'
          },
          value: 45000,
          status: 'under-review',
          priority: 'high',
          stage: 'proposal',
          startDate: '2024-02-29',
          endDate: '2024-08-30',
          probability: 60,
          description: 'Multi-channel outreach campaign for SaaS startup',
          tags: ['saas', 'startup', 'outreach'],
          createdAt: '2024-02-15T00:00:00Z',
          updatedAt: '2024-02-28T00:00:00Z'
        }
      ];
      
      setContracts(mockContracts);
    } catch (error) {
      console.error('Failed to load contracts:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadContracts();
  }, []);

  const handleView = (contract: Contract) => {
    console.log('View contract:', contract.title);
  };

  const handleEdit = (contract: Contract) => {
    console.log('Edit contract:', contract.title);
  };

  const handleDelete = (contract: Contract) => {
    console.log('Delete contract:', contract.title);
  };

  const filteredContracts = contracts.filter(contract => {
    const matchesSearch = contract.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         contract.client.company.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         contract.client.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'All Status' || contract.status === statusFilter.toLowerCase().replace(' ', '-');
    const matchesStage = stageFilter === 'All Stages' || contract.stage === stageFilter.toLowerCase();
    
    return matchesSearch && matchesStatus && matchesStage;
  });

  const statusCounts = {
    'All Status': contracts.length,
    'Negotiating': contracts.filter(c => c.status === 'negotiating').length,
    'Signed': contracts.filter(c => c.status === 'signed').length,
    'Under Review': contracts.filter(c => c.status === 'under-review').length,
    'Expired': contracts.filter(c => c.status === 'expired').length,
  };

  const stageCounts = {
    'All Stages': contracts.length,
    'Proposal': contracts.filter(c => c.stage === 'proposal').length,
    'Negotiation': contracts.filter(c => c.stage === 'negotiation').length,
    'Review': contracts.filter(c => c.stage === 'review').length,
    'Signed': contracts.filter(c => c.stage === 'signed').length,
  };

    return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-950 via-neutral-900 to-neutral-950">
      {/* Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-20 left-20 w-64 h-64 bg-gradient-to-r from-blue-500/10 to-cyan-500/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-20 w-80 h-80 bg-gradient-to-r from-purple-500/10 to-pink-500/10 rounded-full blur-3xl"></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-6 py-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center justify-between mb-8"
        >
          <div>
            <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-400 to-purple-500 bg-clip-text text-transparent mb-2">
              Contracts & Deals
            </h1>
            <p className="text-xl text-neutral-400">
              Track and manage all your contracts and deals
            </p>
          </div>
          <div className="flex items-center space-x-3">
            <Button 
              variant="outline"
              size="lg"
              className="border-neutral-600 text-neutral-300 hover:bg-neutral-800"
            >
              <BarChart3 className="w-5 h-5 mr-2" />
              Pipeline Report
            </Button>
            <Button 
              variant="outline"
              size="lg"
              className="border-neutral-600 text-neutral-300 hover:bg-neutral-800"
            >
              <Download className="w-5 h-5 mr-2" />
              Export Data
            </Button>
            <Button 
              size="lg"
              className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300"
            >
              <Plus className="w-5 h-5 mr-2" />
              New Contract
            </Button>
          </div>
        </motion.div>

        {/* Stats Cards */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8"
        >
          <MetricCard
            title="Total Contracts"
            value={stats.totalContracts}
            change="+18.2% vs last month"
            icon={FileText}
            gradient="bg-gradient-to-r from-blue-500 to-cyan-500"
            description="All active contracts"
          />
          <MetricCard
            title="Total Pipeline Value"
            value={`$${stats.totalPipelineValue.toLocaleString()}`}
            change="+18.2% vs last month"
            icon={DollarSign}
            gradient="bg-gradient-to-r from-purple-500 to-pink-500"
            description="All active contracts"
          />
          <MetricCard
            title="Signed Value"
            value={`$${stats.signedValue.toLocaleString()}`}
            change="+25.4% vs last month"
            icon={CheckCircle}
            gradient="bg-gradient-to-r from-green-500 to-emerald-500"
            description="1 contracts signed"
          />
          <MetricCard
            title="Win Rate"
            value=""
            change="Closed deals won"
            icon={Award}
            gradient="bg-gradient-to-r from-orange-500 to-red-500"
            description="Closed deals won"
            percentage="100%"
          />
        </motion.div>

        {/* Search and Filters */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-gradient-to-br from-neutral-900/80 to-neutral-800/50 backdrop-blur-xl border border-neutral-700/50 rounded-2xl p-6 mb-8 shadow-xl"
        >
          <div className="flex flex-col space-y-4">
            {/* Search Bar */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-neutral-400" />
              <input
                type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search contracts..."
                className="w-full pl-10 pr-4 py-3 bg-neutral-800/50 border border-neutral-700 rounded-xl text-white placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-transparent transition-all"
              />
            </div>

            {/* Filter Tabs */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0 md:space-x-6">
              {/* Status Filter */}
              <div className="flex items-center space-x-1 bg-neutral-800/50 rounded-xl p-1">
                {Object.entries(statusCounts).map(([status, count]) => (
                  <button
                    key={status}
                    onClick={() => setStatusFilter(status)}
                    className={cn(
                      'px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200',
                      statusFilter === status
                        ? 'bg-blue-500 text-white shadow-lg'
                        : 'text-neutral-400 hover:text-white hover:bg-neutral-700/50'
                    )}
                  >
                    {status}
                    {count > 0 && (
                      <span className="ml-2 text-xs bg-neutral-600 text-neutral-300 px-1.5 py-0.5 rounded-full">
                        {count}
                      </span>
                    )}
                  </button>
                ))}
              </div>

              {/* Stage Filter */}
              <div className="flex items-center space-x-1 bg-neutral-800/50 rounded-xl p-1">
                {Object.entries(stageCounts).map(([stage, count]) => (
                  <button
                    key={stage}
                    onClick={() => setStageFilter(stage)}
                    className={cn(
                      'px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200',
                      stageFilter === stage
                        ? 'bg-purple-500 text-white shadow-lg'
                        : 'text-neutral-400 hover:text-white hover:bg-neutral-700/50'
                    )}
                  >
                    {stage}
                    {count > 0 && (
                      <span className="ml-2 text-xs bg-neutral-600 text-neutral-300 px-1.5 py-0.5 rounded-full">
                        {count}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </motion.div>

        {/* Contracts Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <div className="w-16 h-16 border-4 border-blue-500/30 border-t-blue-500 rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-neutral-400">Loading contracts...</p>
            </div>
        </div>
        ) : filteredContracts.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-20"
          >
            <div className="w-20 h-20 bg-gradient-to-r from-blue-500 to-purple-600 rounded-3xl flex items-center justify-center mx-auto mb-6">
              <Building2 className="w-10 h-10 text-white" />
            </div>
            <h3 className="text-3xl font-semibold text-white mb-4">No contracts found</h3>
            <p className="text-neutral-400 mb-8 max-w-md mx-auto">
              {searchQuery 
                ? 'Try adjusting your search criteria or filters' 
                : 'Start by creating your first contract or deal'
              }
            </p>
            <Button
              size="lg"
              className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300"
            >
              <Plus className="w-6 h-6 mr-3" />
              Create Contract
            </Button>
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6"
          >
            {filteredContracts.map((contract, index) => (
              <motion.div
                key={contract.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 * index }}
              >
                <ContractCard
                  contract={contract}
                  onView={handleView}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                />
              </motion.div>
            ))}
          </motion.div>
        )}
      </div>
    </div>
  );
}; 